import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';

interface CoverageArea {
  city: string;
  state: string;
  type: string;
}

interface Stats {
  statesCovered: string;
  citiesServed: string;
}

export default function CoverageMap() {
  const [coverageAreas, setCoverageAreas] = useState<CoverageArea[]>([]);
  const [stats, setStats] = useState<Stats>({ statesCovered: '4', citiesServed: '50+' });
  const [editingField, setEditingField] = useState<{ index: number; field: string } | null>(null);
  const [editValue, setEditValue] = useState('');
  const [editingStat, setEditingStat] = useState<string | null>(null);
  const [statValue, setStatValue] = useState('');

  // Load coverage areas from localStorage or use defaults
  useEffect(() => {
    const savedAreas = localStorage.getItem('coverageAreas');
    const savedStats = localStorage.getItem('coverageStats');
    
    if (savedAreas) {
      setCoverageAreas(JSON.parse(savedAreas));
    } else {
      const defaults: CoverageArea[] = [
        { city: 'Las Vegas', state: 'NV', type: 'Headquarters' },
        { city: 'Henderson', state: 'NV', type: 'Service Area' },
        { city: 'Mesquite', state: 'NV', type: 'Service Area' },
        { city: 'Los Angeles', state: 'CA', type: 'West Coast' },
        { city: 'San Francisco', state: 'CA', type: 'West Coast' },
        { city: 'San Diego', state: 'CA', type: 'West Coast' },
        { city: 'Portland', state: 'OR', type: 'West Coast' },
        { city: 'Seattle', state: 'WA', type: 'West Coast' },
      ];
      setCoverageAreas(defaults);
      localStorage.setItem('coverageAreas', JSON.stringify(defaults));
    }

    if (savedStats) {
      setStats(JSON.parse(savedStats));
    } else {
      localStorage.setItem('coverageStats', JSON.stringify(stats));
    }
  }, []);

  const saveCoverageAreas = (updated: CoverageArea[]) => {
    setCoverageAreas(updated);
    localStorage.setItem('coverageAreas', JSON.stringify(updated));
  };

  const saveStats = (updated: Stats) => {
    setStats(updated);
    localStorage.setItem('coverageStats', JSON.stringify(updated));
  };

  const startEdit = (index: number, field: string, currentValue: string) => {
    setEditingField({ index, field });
    setEditValue(currentValue);
  };

  const saveEdit = () => {
    if (editingField === null) return;
    
    const updated = [...coverageAreas];
    updated[editingField.index] = {
      ...updated[editingField.index],
      [editingField.field]: editValue,
    };
    
    saveCoverageAreas(updated);
    setEditingField(null);
    setEditValue('');
  };

  const cancelEdit = () => {
    setEditingField(null);
    setEditValue('');
  };

  const startStatEdit = (field: string, currentValue: string) => {
    setEditingStat(field);
    setStatValue(currentValue);
  };

  const saveStatEdit = () => {
    if (!editingStat) return;
    
    const updated = { ...stats, [editingStat]: statValue };
    saveStats(updated);
    setEditingStat(null);
    setStatValue('');
  };

  const cancelStatEdit = () => {
    setEditingStat(null);
    setStatValue('');
  };

  const resetToDefaults = () => {
    if (confirm('Reset all coverage areas and stats to default values?')) {
      localStorage.removeItem('coverageAreas');
      localStorage.removeItem('coverageStats');
      window.location.reload();
    }
  };

  const renderEditableField = (index: number, field: string, value: string, className: string = '') => {
    const isEditing = editingField?.index === index && editingField?.field === field;
    
    if (isEditing) {
      return (
        <div className="flex items-center space-x-2" onClick={(e) => e.stopPropagation()}>
          <input
            type="text"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') saveEdit();
              if (e.key === 'Escape') cancelEdit();
            }}
            className="bg-white/20 border border-electric-blue text-white px-2 py-1 rounded text-sm flex-1"
            autoFocus
          />
          <button onClick={saveEdit} className="text-green-400 hover:text-green-300 cursor-pointer">
            <i className="ri-check-line w-4 h-4 flex items-center justify-center"></i>
          </button>
          <button onClick={cancelEdit} className="text-red-400 hover:text-red-300 cursor-pointer">
            <i className="ri-close-line w-4 h-4 flex items-center justify-center"></i>
          </button>
        </div>
      );
    }
    
    return (
      <div
        onClick={(e) => {
          e.stopPropagation();
          startEdit(index, field, value);
        }}
        className={`${className} cursor-pointer hover:bg-white/5 rounded px-2 py-1 transition-all duration-200 group relative inline-block`}
      >
        {value}
        <i className="ri-pencil-line absolute -right-6 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 text-electric-blue text-xs w-4 h-4 flex items-center justify-center"></i>
      </div>
    );
  };

  const renderEditableStat = (field: string, value: string, className: string = '') => {
    const isEditing = editingStat === field;
    
    if (isEditing) {
      return (
        <div className="flex items-center justify-center space-x-2">
          <input
            type="text"
            value={statValue}
            onChange={(e) => setStatValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') saveStatEdit();
              if (e.key === 'Escape') cancelStatEdit();
            }}
            className="bg-white/20 border border-electric-blue text-electric-blue px-2 py-1 rounded text-2xl font-orbitron font-bold text-center w-24"
            autoFocus
          />
          <button onClick={saveStatEdit} className="text-green-400 hover:text-green-300 cursor-pointer">
            <i className="ri-check-line w-4 h-4 flex items-center justify-center"></i>
          </button>
          <button onClick={cancelStatEdit} className="text-red-400 hover:text-red-300 cursor-pointer">
            <i className="ri-close-line w-4 h-4 flex items-center justify-center"></i>
          </button>
        </div>
      );
    }
    
    return (
      <div
        onClick={() => startStatEdit(field, value)}
        className={`${className} cursor-pointer hover:bg-white/5 rounded px-2 py-1 transition-all duration-200 group relative inline-block`}
      >
        {value}
        <i className="ri-pencil-line absolute -right-6 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 text-electric-blue text-xs w-4 h-4 flex items-center justify-center"></i>
      </div>
    );
  };

  return (
    <section className="py-24 bg-dark-900 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="font-orbitron text-5xl font-bold text-white mb-4">
            SERVICE <span className="text-electric-blue">COVERAGE</span>
          </h2>
          <p className="font-inter text-white/60 text-lg mb-4">
            From Las Vegas Valley to the entire West Coast
          </p>
          <button
            onClick={resetToDefaults}
            className="text-white/40 hover:text-electric-blue text-sm font-inter transition-colors duration-200 cursor-pointer"
          >
            <i className="ri-refresh-line mr-2 w-4 h-4 inline-flex items-center justify-center"></i>
            Reset to Defaults
          </button>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Map */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl p-4 overflow-hidden">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d6436.6!2d-115.1398!3d36.1699!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzbCsDEwJzExLjYiTiAxMTXCsDA4JzIzLjMiVw!5e0!3m2!1sen!2sus!4v1234567890"
                width="100%"
                height="500"
                style={{ border: 0, borderRadius: '1rem' }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="LONE TRS Coverage Map"
              />
            </div>
          </motion.div>

          {/* Coverage List */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl p-8">
              <h3 className="font-orbitron text-2xl font-bold text-white mb-6">
                COVERAGE AREAS
              </h3>

              <div className="space-y-4">
                {coverageAreas.map((area, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="flex items-center justify-between p-4 bg-white/5 rounded-xl hover:bg-white/10 transition-colors duration-300"
                  >
                    <div className="flex items-center space-x-4 flex-1">
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-electric-blue/20 to-electric-cyan/20 border border-electric-blue/30 flex items-center justify-center flex-shrink-0">
                        <i className="ri-map-pin-line text-electric-blue text-lg w-5 h-5 flex items-center justify-center"></i>
                      </div>
                      <div className="flex-1">
                        <div className="font-inter text-white font-semibold text-base flex items-center space-x-1">
                          {renderEditableField(index, 'city', area.city, 'font-inter text-white font-semibold text-base')}
                          <span className="text-white">,</span>
                          {renderEditableField(index, 'state', area.state, 'font-inter text-white font-semibold text-base')}
                        </div>
                        <div className="font-inter text-white/60 text-xs">
                          {renderEditableField(index, 'type', area.type, 'font-inter text-white/60 text-xs')}
                        </div>
                      </div>
                    </div>
                    <i className="ri-checkbox-circle-fill text-electric-blue text-xl w-5 h-5 flex items-center justify-center flex-shrink-0"></i>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gradient-to-br from-electric-blue/20 to-electric-cyan/20 border border-electric-blue/30 rounded-2xl p-6 text-center">
                <div className="font-orbitron text-4xl font-bold text-electric-blue mb-2">
                  {renderEditableStat('statesCovered', stats.statesCovered, 'font-orbitron text-4xl font-bold text-electric-blue')}
                </div>
                <div className="font-inter text-white/80 text-sm">
                  States Covered
                </div>
              </div>
              <div className="bg-gradient-to-br from-electric-blue/20 to-electric-cyan/20 border border-electric-blue/30 rounded-2xl p-6 text-center">
                <div className="font-orbitron text-4xl font-bold text-electric-blue mb-2">
                  {renderEditableStat('citiesServed', stats.citiesServed, 'font-orbitron text-4xl font-bold text-electric-blue')}
                </div>
                <div className="font-inter text-white/80 text-sm">
                  Cities Served
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
